import React, { useState, useEffect } from 'react';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Download, FileText, DollarSign, Building, ShoppingCart, Eye } from 'lucide-react';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import {
  fetchProfitReport,
  fetchProfitReportByBranch,
  fetchProfitStatementReport,
  fetchPayablesReport,
  fetchSalesByBranchesReport,
  fetchSuppliers,
  fetchBranches,
  fetchItems,
  fetchSalesByItemReport,
  getApiBaseUrl,
} from '@/api';
import { MonthYearPicker } from '@/components/ui/month-year-picker';
import { StatementDetailDialog } from '@/components/reports/StatementDetailDialog';

interface ProfitReportItem {
  item_id: string;
  name?: string | null;
  quantity: number;
  display_quantity?: number;
  buying_price: number;
  selling_price: number;
  profit: number;
}

interface ProfitReportSaleDetail {
  type?: 'sale';
  sale_id: string;
  invoice_number?: string | number | null;
  created_at: string;
  total_amount: number;
  cost: number;
  profit: number;
  items: ProfitReportItem[];
}

interface ProfitReportLayawayDetail {
  type: 'layaway_payment';
  payment_id: string;
  layaway_id: string;
  layaway_number?: string | number | null;
  payment_ratio?: number;
  created_at: string;
  total_amount: number;
  cost: number;
  profit: number;
  items: ProfitReportItem[];
}

type ProfitReportDetail = ProfitReportSaleDetail | ProfitReportLayawayDetail;

interface ProfitReport {
  summary: {
    total_revenue: number;
    total_cost: number;
    total_profit: number;
  };
  details?: ProfitReportDetail[];
}

interface ProfitStatementPeriod {
  period_id: string;
  label: string;
  start_date: string;
  end_date: string;
  revenue: {
    sales: number;
    layaway: number;
    total: number;
  };
  cost_of_sales: {
    sales: number;
    layaway: number;
    total: number;
  };
  gross_profit: number;
  expenses: {
    category_id: string;
    category_name: string;
    total: number;
  }[];
  total_expenses: number;
  net_profit: number;
  supplier_payments: {
    category_name: string;
    total: number;
  };
}

interface ProfitStatementReport {
  period_type: 'monthly' | 'quarterly' | 'half-yearly' | 'yearly';
  periods: ProfitStatementPeriod[];
}

interface ProfitByBranchReport {
  summary: {
    total_revenue: number;
    total_cost: number;
    total_profit: number;
  };
  branches: {
    branch_id: string;
    branch_name: string;
    total_revenue: number;
    total_cost: number;
    total_profit: number;
    sales_count: number;
    layaway_payments_count: number;
  }[];
}

interface PayablesReport {
  summary?: {
    total_payables: number;
    total_paid: number;
    total_due: number;
  };
  details?: {
    invoice_id: string;
    supplier_id: string;
    supplier_name: string;
    invoice_number: string;
    date: string;
    due_date: string;
    amount: number;
    paid_amount: number;
    remaining_amount: number;
    status: string;
    attachment_path?: string;
  }[];
}

type BranchSaleDetail =
  | {
      type: 'sale';
      sale_id: string;
      invoice_number?: string | number | null;
      created_at: string;
      total_amount: number;
    }
  | {
      type: 'layaway_payment';
      payment_id: string;
      layaway_number?: string | number | null;
      created_at: string;
      total_amount: number;
    };

interface SalesByBranchesReport {
  summary?: {
    total_sales_amount: number;
    total_transactions: number;
    branches: number;
  };
  details?: {
    branch_id: string;
    branch_name: string;
    total_amount: number;
    transactions: number;
    sales: BranchSaleDetail[];
  }[];
}

type ItemSaleDetail =
  | {
      type: 'sale';
      sale_id: string;
      invoice_number?: string | number | null;
      created_at: string;
      quantity: number;
      price: number;
    }
  | {
      type: 'layaway_payment';
      layaway_number?: string | number | null;
      created_at: string;
      quantity: number;
      price: number;
      display_quantity?: number;
      payment_ratio?: number;
    };

interface SalesByItemReport {
  summary?: {
    total_sales_amount: number;
    total_quantity_sold: number;
    total_items: number;
  };
  details?: {
    item_id: string;
    name: string;
    total_quantity: number;
    total_amount: number;
    average_price: number;
    sales: ItemSaleDetail[];
  }[];
}

interface Supplier {
  id: string;
  name: string;
}

interface Branch {
  id: string;
  name: string;
}

export default function ReportsPage() {
  const currencySymbol = useCurrencySymbol();
  useEffect(() => {
    document.title = 'Reports - Vixalor';
  }, []);
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 12 }, (_, index) => currentYear - 5 + index);
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];
  const [profitReport, setProfitReport] = useState<ProfitReport | null>(null);
  const [profitByBranchReport, setProfitByBranchReport] = useState<ProfitByBranchReport | null>(null);
  const [profitStatementReport, setProfitStatementReport] = useState<ProfitStatementReport | null>(null);
  const [payablesReport, setPayablesReport] = useState<PayablesReport | null>(null);
  const [salesByBranchesReport, setSalesByBranchesReport] = useState<SalesByBranchesReport | null>(null);
  const [salesByItemReport, setSalesByItemReport] = useState<SalesByItemReport | null>(null);

  const [profitStartDate, setProfitStartDate] = useState<Date | undefined>(undefined);
  const [profitEndDate, setProfitEndDate] = useState<Date | undefined>(undefined);
  const [profitBranchStartDate, setProfitBranchStartDate] = useState<Date | undefined>(undefined);
  const [profitBranchEndDate, setProfitBranchEndDate] = useState<Date | undefined>(undefined);
  const [salesStartDate, setSalesStartDate] = useState<Date | undefined>(undefined);
  const [salesEndDate, setSalesEndDate] = useState<Date | undefined>(undefined);
  const [itemStartDate, setItemStartDate] = useState<Date | undefined>(undefined);
  const [itemEndDate, setItemEndDate] = useState<Date | undefined>(undefined);
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [statementPeriodType, setStatementPeriodType] = useState<'monthly' | 'quarterly' | 'half-yearly' | 'yearly'>('monthly');
  const [statementPeriods, setStatementPeriods] = useState<Array<{ year: number; month?: number; quarter?: number; half?: number }>>([
    { year: new Date().getFullYear(), month: new Date().getMonth() + 1 },
  ]);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<{ id: string; name: string }[]>([]);

  // Statement detail dialog state
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean;
    type: 'sales' | 'layaway' | 'expense';
    startDate: string;
    endDate: string;
    label: string;
    categoryId?: string;
    categoryName?: string;
  }>({
    open: false,
    type: 'sales',
    startDate: '',
    endDate: '',
    label: '',
  });
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch suppliers, branches, and items on mount
  useEffect(() => {
    async function loadData() {
      try {
        const [suppliersRes, branchesRes, itemsRes] = await Promise.all([
          fetchSuppliers(),
          fetchBranches(),
          fetchItems(),
        ]);
        setSuppliers(suppliersRes.data);
        setBranches(branchesRes.data);
        setItems(itemsRes.data.map(item => ({ id: item.id, name: item.name })));
      } catch (err: any) {
        toast.error('Failed to load suppliers, branches, or items');
      }
    }
    loadData();
  }, []);

  const buildStatementPeriodId = (period: { year: number; month?: number; quarter?: number; half?: number }) => {
    if (statementPeriodType === 'monthly') {
      const month = String(period.month ?? 1).padStart(2, '0');
      return `${period.year}-${month}`;
    }
    if (statementPeriodType === 'quarterly') {
      return `${period.year}-Q${period.quarter ?? 1}`;
    }
    if (statementPeriodType === 'half-yearly') {
      return `${period.year}-H${period.half ?? 1}`;
    }
    return `${period.year}`;
  };

  const createDefaultStatementPeriod = (type: typeof statementPeriodType) => {
    const monthIndex = new Date().getMonth() + 1;
    if (type === 'monthly') {
      return { year: currentYear, month: monthIndex };
    }
    if (type === 'quarterly') {
      return { year: currentYear, quarter: Math.ceil(monthIndex / 3) };
    }
    if (type === 'half-yearly') {
      return { year: currentYear, half: monthIndex <= 6 ? 1 : 2 };
    }
    return { year: currentYear };
  };

  const updateStatementPeriodType = (value: 'monthly' | 'quarterly' | 'half-yearly' | 'yearly') => {
    setStatementPeriodType(value);
    setStatementPeriods([createDefaultStatementPeriod(value)]);
    setProfitStatementReport(null);
  };

  const updateStatementPeriod = (index: number, updates: Partial<{ year: number; month: number; quarter: number; half: number }>) => {
    setStatementPeriods(prev => prev.map((period, idx) => (idx === index ? { ...period, ...updates } : period)));
  };

  const addStatementPeriod = () => {
    setStatementPeriods(prev => {
      if (prev.length >= 4) {
        toast.error('You can compare up to 4 periods.');
        return prev;
      }
      return [...prev, createDefaultStatementPeriod(statementPeriodType)];
    });
  };

  const removeStatementPeriod = (index: number) => {
    setStatementPeriods(prev => prev.filter((_, idx) => idx !== index));
  };

  // Fetch Profit Report
  const loadProfitReport = async () => {
    try {
      setLoading(true);
      const start = profitStartDate ? format(profitStartDate, 'yyyy-MM-dd') : '';
      const end = profitEndDate ? format(profitEndDate, 'yyyy-MM-dd') : '';
      const response = await fetchProfitReport(start, end);
      setProfitReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load profit report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Profit by Branch Report
  const loadProfitByBranchReport = async () => {
    try {
      setLoading(true);
      const start = profitBranchStartDate ? format(profitBranchStartDate, 'yyyy-MM-dd') : '';
      const end = profitBranchEndDate ? format(profitBranchEndDate, 'yyyy-MM-dd') : '';
      const response = await fetchProfitReportByBranch(start, end);
      setProfitByBranchReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load profit by branch report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Payables Report
  const loadPayablesReport = async () => {
    try {
      setLoading(true);
      const supplierId = selectedSupplier === 'all' ? '' : selectedSupplier;
      const response = await fetchPayablesReport(supplierId);
      setPayablesReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load payables report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Sales by Branches Report
  const loadSalesByBranchesReport = async () => {
    try {
      setLoading(true);
      const start = salesStartDate ? format(salesStartDate, 'yyyy-MM-dd') : '';
      const end = salesEndDate ? format(salesEndDate, 'yyyy-MM-dd') : '';
      const branch = selectedBranch === 'all' ? '' : selectedBranch;
      const response = await fetchSalesByBranchesReport(start, end, branch);
      setSalesByBranchesReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load sales by branches report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Sales by Item Report
  const loadSalesByItemReport = async () => {
    try {
      setLoading(true);
      const start = itemStartDate ? format(itemStartDate, 'yyyy-MM-dd') : '';
      const end = itemEndDate ? format(itemEndDate, 'yyyy-MM-dd') : '';
      const response = await fetchSalesByItemReport(start, end); // Fixed reference
      setSalesByItemReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load sales by item report');
    } finally {
      setLoading(false);
    }
  };

  const loadProfitStatementReport = async () => {
    try {
      setLoading(true);
      const periodIds = statementPeriods.map(buildStatementPeriodId);
      const uniqueIds = new Set(periodIds);
      if (uniqueIds.size !== periodIds.length) {
        toast.error('Please select unique periods for comparison.');
        setLoading(false);
        return;
      }
      const response = await fetchProfitStatementReport(statementPeriodType, periodIds);
      setProfitStatementReport(response.data);
    } catch (err: any) {
      toast.error('Failed to load profit statement');
    } finally {
      setLoading(false);
    }
  };

  // Generate PDF for Profit Report
  const downloadProfitReportPDF = () => {
    if (!profitReport || !profitReport.details) return;

    const doc = new jsPDF();
    const margin = 20;
    let cursorY = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Profit Report', margin, cursorY);
    cursorY += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Revenue: ${currencySymbol}${(profitReport.summary?.total_revenue ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Cost: ${currencySymbol}${(profitReport.summary?.total_cost ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Profit: ${currencySymbol}${(profitReport.summary?.total_profit ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 20;

    profitReport.details.forEach((detail) => {
      const isLayaway = detail.type === 'layaway_payment';
      let title: string;

      if (isLayaway) {
        title = `Layaway #${detail.layaway_number ?? detail.layaway_id} Payment`;
      } else {
        const saleDetail = detail as ProfitReportSaleDetail;
        title = saleDetail.invoice_number
          ? `Sale Invoice #${saleDetail.invoice_number}`
          : `Sale #${saleDetail.sale_id}`;
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(title, margin, cursorY);
      cursorY += 10;

      autoTable(doc, {
        startY: cursorY,
        head: [['Item Name', 'Quantity', 'Buying Price', 'Selling Price', 'Profit']],
        body: detail.items.map(item => [
          item.name ?? '—',
          (() => {
            if (isLayaway) {
              const base = item.display_quantity ?? item.quantity;
              const coverage = typeof detail.payment_ratio === 'number'
                ? ` (${(detail.payment_ratio * 100).toFixed(0)}%)`
                : '';
              return `${Number(base).toLocaleString()}${coverage}`;
            }
            return Number(item.quantity).toLocaleString();
          })(),
          `${currencySymbol}${item.buying_price.toFixed(2)}`,
          `${currencySymbol}${item.selling_price.toFixed(2)}`,
          `${currencySymbol}${item.profit.toFixed(2)}`
        ]),
        margin: { left: margin, right: margin },
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [33, 150, 243], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });

      cursorY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`${isLayaway ? 'Payment Date' : 'Sale Date'}: ${new Date(detail.created_at).toLocaleString()}`, margin, cursorY);
      cursorY += 6;
      doc.text(`${isLayaway ? 'Payment Amount' : 'Total Amount'}: ${currencySymbol}${(detail.total_amount ?? 0).toFixed(2)}`, margin, cursorY);
      cursorY += 6;
      doc.text(`Cost: ${currencySymbol}${(detail.cost ?? 0).toFixed(2)}`, margin, cursorY);
      cursorY += 6;
      doc.text(`Profit: ${currencySymbol}${(detail.profit ?? 0).toFixed(2)}`, margin, cursorY);
      cursorY += 15;
    });

    doc.save('profit_report.pdf');
  };

  // Generate PDF for Payables Report
  const downloadPayablesReportPDF = () => {
    if (!payablesReport || !payablesReport.details) return;

    const doc = new jsPDF();
    const margin = 20;
    let cursorY = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Payables to Supplier Report', margin, cursorY);
    cursorY += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Payables: ${currencySymbol}${(payablesReport.summary?.total_payables ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Paid: ${currencySymbol}${(payablesReport.summary?.total_paid ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Due: ${currencySymbol}${(payablesReport.summary?.total_due ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 20;

    autoTable(doc, {
      startY: cursorY,
      head: [['Supplier', 'Invoice #', 'Date', 'Due Date', 'Amount', 'Paid', 'Remaining', 'Status']],
      body: payablesReport.details.map(detail => [
        detail.supplier_name,
        detail.invoice_number,
        detail.date,
        detail.due_date,
        `${currencySymbol}${detail.amount.toFixed(2)}`,
        `${currencySymbol}${detail.paid_amount.toFixed(2)}`,
        `${currencySymbol}${detail.remaining_amount.toFixed(2)}`,
        detail.status
      ]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 10, cellPadding: 4 },
      headStyles: { fillColor: [33, 150, 243], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' }
      }
    });

    doc.save('payables_report.pdf');
  };

  // Generate PDF for Sales by Branches Report
  const downloadSalesByBranchesPDF = () => {
    if (!salesByBranchesReport || !salesByBranchesReport.details) return;

    const doc = new jsPDF();
    const margin = 20;
    let cursorY = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Sales by Branches Report', margin, cursorY);
    cursorY += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Sales Amount: ${currencySymbol}${(salesByBranchesReport.summary?.total_sales_amount ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Total Transactions: ${salesByBranchesReport.summary?.total_transactions ?? 0}`, margin, cursorY);
    cursorY += 10;
    doc.text(`Branches: ${salesByBranchesReport.summary?.branches ?? 0}`, margin, cursorY);
    cursorY += 20;

    salesByBranchesReport.details.forEach(branch => {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${branch.branch_name}`, margin, cursorY);
      cursorY += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Amount: ${currencySymbol}${(branch.total_amount ?? 0).toFixed(2)}`, margin, cursorY);
      cursorY += 6;
      doc.text(`Transactions: ${branch.transactions ?? 0}`, margin, cursorY);
      cursorY += 10;

      autoTable(doc, {
        startY: cursorY,
        head: [['Type', 'Reference', 'Date', 'Amount']],
        body: branch.sales.map(entry => {
          const isLayaway = entry.type === 'layaway_payment';
          const typeLabel = isLayaway ? 'Layaway Payment' : 'Sale';
          const reference = isLayaway
            ? `Layaway #${entry.layaway_number ?? '—'} Payment`
            : entry.invoice_number
              ? `Invoice #${entry.invoice_number}`
              : `Sale #${entry.sale_id}`;

          return [
            typeLabel,
            reference,
            new Date(entry.created_at).toLocaleString(),
            `${currencySymbol}${entry.total_amount.toFixed(2)}`
          ];
        }),
        margin: { left: margin, right: margin },
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [33, 150, 243], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          3: { halign: 'right' }
        }
      });

      cursorY = (doc as any).lastAutoTable.finalY + 15;
    });

    doc.save('sales_by_branches_report.pdf');
  };

  // Generate PDF for Sales by Item Report
  const downloadSalesByItemPDF = () => {
    if (!salesByItemReport || !salesByItemReport.details) return;

    const doc = new jsPDF();
    const margin = 20;
    let cursorY = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Sales by Item Report', margin, cursorY);
    cursorY += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Sales Amount: ${currencySymbol}${(salesByItemReport.summary?.total_sales_amount ?? 0).toFixed(2)}`, margin, cursorY);
    cursorY += 10;
    doc.text(
      `Total Quantity Sold: ${Number(salesByItemReport.summary?.total_quantity_sold ?? 0).toLocaleString(undefined, {
        maximumFractionDigits: 2
      })}`,
      margin,
      cursorY
    );
    cursorY += 10;
    doc.text(`Total Items: ${salesByItemReport.summary?.total_items ?? 0}`, margin, cursorY);
    cursorY += 20;

    salesByItemReport.details.forEach(item => {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${item.name}`, margin, cursorY);
      cursorY += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Total Quantity: ${Number(item.total_quantity).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
        margin,
        cursorY
      );
      cursorY += 6;
      doc.text(`Total Amount: ${currencySymbol}${(item.total_amount ?? 0).toFixed(2)}`, margin, cursorY);
      cursorY += 6;
      doc.text(`Average Price: ${currencySymbol}${(item.average_price ?? 0).toFixed(2)}`, margin, cursorY);
      cursorY += 10;

      autoTable(doc, {
        startY: cursorY,
        head: [['Type', 'Reference', 'Date', 'Quantity', 'Price']],
        body: item.sales.map(sale => {
          const isLayaway = sale.type === 'layaway_payment';
          const typeLabel = isLayaway ? 'Layaway Payment' : 'Sale';
          const reference = isLayaway
            ? `Layaway #${sale.layaway_number ?? '—'} Payment`
            : sale.invoice_number
              ? `Invoice #${sale.invoice_number}`
              : `Sale #${sale.sale_id}`;

          const baseQuantity = isLayaway
            ? sale.display_quantity ?? sale.quantity
            : sale.quantity;
          const coverageText = isLayaway && typeof sale.payment_ratio === 'number'
            ? ` (${(sale.payment_ratio * 100).toFixed(0)}%)`
            : '';
          const quantityDisplay = `${Number(baseQuantity).toLocaleString(undefined, {
            maximumFractionDigits: isLayaway ? 2 : 0
          })}${coverageText}`;

          return [
            typeLabel,
            reference,
            new Date(sale.created_at).toLocaleString(),
            quantityDisplay,
            `${currencySymbol}${sale.price.toFixed(2)}`
          ];
        }),
        margin: { left: margin, right: margin },
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [33, 150, 243], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          4: { halign: 'right' }
        }
      });

      cursorY = (doc as any).lastAutoTable.finalY + 15;
    });

    doc.save('sales_by_item_report.pdf');
  };

  const statementPeriodsData = profitStatementReport?.periods ?? [];
  const statementExpenseCategories = Array.from(
    new Map(
      statementPeriodsData
        .flatMap(period => period.expenses)
        .map(expense => [expense.category_id, expense.category_name])
    ).entries()
  ).map(([id, name]) => ({ id, name }));

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold">Reports</h1>
        <p className="text-muted-foreground">Generate and view various reports for your Vixalor system.</p>
      </div>

      <Tabs defaultValue="profit" className="space-y-4">
        <TabsList>
          <TabsTrigger value="profit">Profit Report</TabsTrigger>
          <TabsTrigger value="profit-statement">Profit Statement</TabsTrigger>
          <TabsTrigger value="profit-by-branch">Profit by Branch</TabsTrigger>
          <TabsTrigger value="payables">Payables to Supplier</TabsTrigger>
          <TabsTrigger value="sales-branches">Sales by Branches</TabsTrigger>
          <TabsTrigger value="sales-items">Sales by Item</TabsTrigger>
        </TabsList>

        {/* Profit Report Tab */}
        <TabsContent value="profit">
          <Card>
            <CardHeader>
              <CardTitle>Profit Report</CardTitle>
              <CardDescription>View profits from sales over a specified period.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Start Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {profitStartDate ? format(profitStartDate, 'PPP') : 'Select start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={profitStartDate}
                        onSelect={setProfitStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">End Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {profitEndDate ? format(profitEndDate, 'PPP') : 'Select end date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={profitEndDate}
                        onSelect={setProfitEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Button onClick={loadProfitReport} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate Report'}
                </Button>
              </div>

              {profitReport && profitReport.summary && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{profitReport.summary.total_revenue.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{profitReport.summary.total_cost.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Profit</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{profitReport.summary.total_profit.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-4">
                    <Button onClick={downloadProfitReportPDF} disabled={!profitReport.details?.length}>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>

                  {profitReport.details?.map(detail => {
                    const isLayaway = detail.type === 'layaway_payment';
                    const cardKey = isLayaway
                      ? `layaway-${detail.payment_id}`
                      : `sale-${(detail as ProfitReportSaleDetail).sale_id}`;

                    const saleDetail = !isLayaway ? (detail as ProfitReportSaleDetail) : null;
                    const headerTitle = isLayaway
                      ? `Layaway #${detail.layaway_number ?? detail.layaway_id} Payment`
                      : saleDetail?.invoice_number
                        ? `Sale Invoice #${saleDetail.invoice_number}`
                        : `Sale #${saleDetail?.sale_id}`;

                    const amountLabel = isLayaway ? 'Payment Amount' : 'Total Amount';

                    return (
                      <Card key={cardKey} className="mb-4">
                        <CardHeader>
                          <div className="flex justify-between items-center">
                            <CardTitle>{headerTitle}</CardTitle>
                            <p className="text-sm text-muted-foreground">
                              Date: {new Date(detail.created_at).toLocaleString()}
                            </p>
                          </div>
                          <CardDescription>
                            {amountLabel}: {currencySymbol}{detail.total_amount.toFixed(2)} | Cost: {currencySymbol}{detail.cost.toFixed(2)} | Profit: {currencySymbol}{detail.profit.toFixed(2)}
                            {isLayaway && typeof detail.payment_ratio === 'number'
                              ? ` | Payment Coverage: ${(detail.payment_ratio * 100).toFixed(0)}%`
                              : ''}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Item Name</TableHead>
                                <TableHead>Quantity</TableHead>
                                <TableHead className="text-right">Buying Price</TableHead>
                                <TableHead className="text-right">Selling Price</TableHead>
                                <TableHead className="text-right">Profit</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {detail.items.map(item => {
                                const baseQuantity = isLayaway
                                  ? item.display_quantity ?? item.quantity
                                  : item.quantity;
                                const coverageText = isLayaway && typeof detail.payment_ratio === 'number'
                                  ? ` (${(detail.payment_ratio * 100).toFixed(0)}%)`
                                  : '';
                                const quantityDisplay = `${Number(baseQuantity).toLocaleString()}${coverageText}`;

                                return (
                                  <TableRow key={item.item_id}>
                                    <TableCell>{item.name ?? '—'}</TableCell>
                                    <TableCell>{quantityDisplay}</TableCell>
                                    <TableCell className="text-right">{currencySymbol}{item.buying_price.toFixed(2)}</TableCell>
                                    <TableCell className="text-right">{currencySymbol}{item.selling_price.toFixed(2)}</TableCell>
                                    <TableCell className="text-right">{currencySymbol}{item.profit.toFixed(2)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profit Statement Tab */}
        <TabsContent value="profit-statement">
          <Card>
            <CardHeader>
              <CardTitle>Profit Statement</CardTitle>
              <CardDescription>Compare revenue, costs, and expenses across periods.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Period Type</label>
                    <Select
                      value={statementPeriodType}
                      onValueChange={value => updateStatementPeriodType(value as 'monthly' | 'quarterly' | 'half-yearly' | 'yearly')}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select period type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="half-yearly">Half-Yearly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={addStatementPeriod} disabled={loading}>
                    Add Comparison
                  </Button>
                  <Button onClick={loadProfitStatementReport} disabled={loading}>
                    Generate Statement
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Includes approved company expenses and net sales after returns.
                </p>

                {statementPeriods.map((period, index) => (
                  <div key={`${period.year}-${index}`} className="flex flex-wrap items-end gap-3">
                    {statementPeriodType === 'monthly' && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Month & Year</label>
                        <MonthYearPicker
                          year={period.year}
                          month={period.month ?? 1}
                          onYearChange={(year) => updateStatementPeriod(index, { year })}
                          onMonthChange={(month) => updateStatementPeriod(index, { month })}
                          availableYears={availableYears}
                        />
                      </div>
                    )}

                    {statementPeriodType !== 'monthly' && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Year</label>
                        <Select
                          value={String(period.year)}
                          onValueChange={value => updateStatementPeriod(index, { year: Number(value) })}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {availableYears.map(year => (
                              <SelectItem key={year} value={String(year)}>
                                {year}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {statementPeriodType === 'quarterly' && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Quarter</label>
                        <Select
                          value={String(period.quarter ?? 1)}
                          onValueChange={value => updateStatementPeriod(index, { quarter: Number(value) })}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4].map(quarter => (
                              <SelectItem key={quarter} value={String(quarter)}>
                                Q{quarter}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {statementPeriodType === 'half-yearly' && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Half</label>
                        <Select
                          value={String(period.half ?? 1)}
                          onValueChange={value => updateStatementPeriod(index, { half: Number(value) })}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2].map(half => (
                              <SelectItem key={half} value={String(half)}>
                                H{half}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {statementPeriods.length > 1 && (
                      <Button variant="ghost" onClick={() => removeStatementPeriod(index)}>
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {profitStatementReport && statementPeriodsData.length > 0 ? (
                <div className="overflow-x-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-64">Line Item</TableHead>
                        {statementPeriodsData.map(period => (
                          <TableHead key={period.period_id} className="text-right">
                            {period.label}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Net sales</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <span>{currencySymbol}{period.revenue.sales.toFixed(2)}</span>
                              {period.revenue.sales > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => setDetailDialog({
                                    open: true,
                                    type: 'sales',
                                    startDate: period.start_date,
                                    endDate: period.end_date,
                                    label: `Net Sales - ${period.label}`,
                                  })}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Layaway payments</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <span>{currencySymbol}{period.revenue.layaway.toFixed(2)}</span>
                              {period.revenue.layaway > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => setDetailDialog({
                                    open: true,
                                    type: 'layaway',
                                    startDate: period.start_date,
                                    endDate: period.end_date,
                                    label: `Layaway Payments - ${period.label}`,
                                  })}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow className="bg-muted/40">
                        <TableCell className="font-semibold">Total revenue</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right font-semibold">
                            {currencySymbol}{period.revenue.total.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Cost of sales (sales)</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right">
                            {currencySymbol}{period.cost_of_sales.sales.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Cost of sales (layaways)</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right">
                            {currencySymbol}{period.cost_of_sales.layaway.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow className="bg-muted/40">
                        <TableCell className="font-semibold">Total cost of sales</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right font-semibold">
                            {currencySymbol}{period.cost_of_sales.total.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow className="bg-muted/50">
                        <TableCell className="font-semibold">Gross profit</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right font-semibold">
                            {currencySymbol}{period.gross_profit.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>

                      <TableRow>
                        <TableCell className="font-semibold">Operating expenses</TableCell>
                        <TableCell colSpan={statementPeriodsData.length} />
                      </TableRow>

                      {statementExpenseCategories.map(category => (
                        <TableRow key={category.id}>
                          <TableCell className="text-muted-foreground">{category.name}</TableCell>
                          {statementPeriodsData.map(period => {
                            const amount = period.expenses.find(expense => expense.category_id === category.id)?.total ?? 0;
                            return (
                              <TableCell key={period.period_id} className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <span>{currencySymbol}{amount.toFixed(2)}</span>
                                  {amount > 0 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      onClick={() => setDetailDialog({
                                        open: true,
                                        type: 'expense',
                                        startDate: period.start_date,
                                        endDate: period.end_date,
                                        label: `${category.name} - ${period.label}`,
                                        categoryId: category.id,
                                        categoryName: category.name,
                                      })}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}

                      <TableRow className="bg-muted/40">
                        <TableCell className="font-semibold">Total operating expenses</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right font-semibold">
                            {currencySymbol}{period.total_expenses.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>

                      <TableRow className="bg-muted/50">
                        <TableCell className="font-semibold">Net profit</TableCell>
                        {statementPeriodsData.map(period => (
                          <TableCell key={period.period_id} className="text-right font-semibold">
                            {currencySymbol}{period.net_profit.toFixed(2)}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">
                          {statementPeriodsData[0]?.supplier_payments?.category_name ?? 'Supplier payments'}
                        </TableCell>
                        {statementPeriodsData.map(period => {
                          const amount = period.supplier_payments?.total ?? 0;
                          return (
                            <TableCell key={period.period_id} className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <span>{currencySymbol}{amount.toFixed(2)}</span>
                                {amount > 0 && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => setDetailDialog({
                                      open: true,
                                      type: 'expense',
                                      startDate: period.start_date,
                                      endDate: period.end_date,
                                      label: `Payment to Supplier - ${period.label}`,
                                      categoryId: 'cat_274e0cb02f5bbd4e6085f32e28894fca',
                                      categoryName: 'Payment to Supplier',
                                    })}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Select periods and generate the statement to view results.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profit by Branch Report Tab */}
        <TabsContent value="profit-by-branch">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Profit Report by Branch
              </CardTitle>
              <CardDescription>View profit breakdown across different branches.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Start Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-[280px] justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {profitBranchStartDate ? format(profitBranchStartDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={profitBranchStartDate}
                        onSelect={setProfitBranchStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">End Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-[280px] justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {profitBranchEndDate ? format(profitBranchEndDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={profitBranchEndDate}
                        onSelect={setProfitBranchEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <Button onClick={loadProfitByBranchReport} disabled={loading}>
                  Generate Report
                </Button>
              </div>

              {profitByBranchReport && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          Total Revenue
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-2xl font-bold">
                          {currencySymbol}{profitByBranchReport.summary.total_revenue.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <ShoppingCart className="h-4 w-4" />
                          Total Cost
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="text-2xl font-bold text-red-600">
                          {currencySymbol}{profitByBranchReport.summary.total_cost.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Total Profit
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className={`text-2xl font-bold ${profitByBranchReport.summary.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {currencySymbol}{profitByBranchReport.summary.total_profit.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Branch Breakdown Table */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Profit by Branch</h3>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const doc = new jsPDF();
                          doc.setFontSize(20);
                          doc.text('Profit Report by Branch', 20, 20);
                          doc.setFontSize(12);
                          doc.text(`Generated: ${format(new Date(), 'PPP')}`, 20, 35);
                          if (profitBranchStartDate) doc.text(`From: ${format(profitBranchStartDate, 'PPP')}`, 20, 45);
                          if (profitBranchEndDate) doc.text(`To: ${format(profitBranchEndDate, 'PPP')}`, 20, 55);
                          
                          const tableData = profitByBranchReport.branches.map(branch => [
                            branch.branch_name,
                            `${currencySymbol}${branch.total_revenue.toFixed(2)}`,
                            `${currencySymbol}${branch.total_cost.toFixed(2)}`,
                            `${currencySymbol}${branch.total_profit.toFixed(2)}`,
                            branch.sales_count.toString(),
                            branch.layaway_payments_count.toString(),
                          ]);

                          autoTable(doc, {
                            head: [['Branch', 'Revenue', 'Cost', 'Profit', 'Sales', 'Layaway Payments']],
                            body: tableData,
                            startY: 65,
                          });

                          doc.save('profit-by-branch-report.pdf');
                          toast.success('Report downloaded successfully');
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Branch</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                          <TableHead className="text-right">Profit</TableHead>
                          <TableHead className="text-center">Sales Count</TableHead>
                          <TableHead className="text-center">Layaway Payments</TableHead>
                          <TableHead className="text-right">Profit Margin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {profitByBranchReport.branches.map((branch, index) => {
                          const profitMargin = branch.total_revenue > 0 
                            ? ((branch.total_profit / branch.total_revenue) * 100).toFixed(1)
                            : '0.0';
                          
                          return (
                            <TableRow key={branch.branch_id}>
                              <TableCell className="font-medium">{branch.branch_name}</TableCell>
                              <TableCell className="text-right">{currencySymbol}{branch.total_revenue.toFixed(2)}</TableCell>
                              <TableCell className="text-right text-red-600">{currencySymbol}{branch.total_cost.toFixed(2)}</TableCell>
                              <TableCell className={`text-right font-semibold ${branch.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {currencySymbol}{branch.total_profit.toFixed(2)}
                              </TableCell>
                              <TableCell className="text-center">{branch.sales_count}</TableCell>
                              <TableCell className="text-center">{branch.layaway_payments_count}</TableCell>
                              <TableCell className={`text-right font-medium ${parseFloat(profitMargin) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {profitMargin}%
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payables to Supplier Tab */}
        <TabsContent value="payables">
          <Card>
            <CardHeader>
              <CardTitle>Payables to Supplier Report</CardTitle>
              <CardDescription>View outstanding payments to suppliers.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Supplier:</label>
                  <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Suppliers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Suppliers</SelectItem>
                      {suppliers.map(supplier => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadPayablesReport} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate Report'}
                </Button>
              </div>

              {payablesReport && payablesReport.summary && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Payables</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{payablesReport.summary.total_payables.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{payablesReport.summary.total_paid.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Due</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{payablesReport.summary.total_due.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-4">
                    <Button onClick={downloadPayablesReportPDF} disabled={!payablesReport.details?.length}>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead className="text-right">Remaining</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Attachment</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payablesReport.details?.map(detail => (
                        <TableRow key={detail.invoice_id}>
                          <TableCell>{detail.supplier_name}</TableCell>
                          <TableCell>{detail.invoice_number}</TableCell>
                          <TableCell>{detail.date}</TableCell>
                          <TableCell>{detail.due_date}</TableCell>
                          <TableCell className="text-right">{currencySymbol}{detail.amount.toFixed(2)}</TableCell>
                          <TableCell className="text-right">{currencySymbol}{detail.paid_amount.toFixed(2)}</TableCell>
                          <TableCell className="text-right">{currencySymbol}{detail.remaining_amount.toFixed(2)}</TableCell>
                          <TableCell>{detail.status}</TableCell>
                          <TableCell>
                            {detail.attachment_path ? (
                              <a href={`${getApiBaseUrl()}/${detail.attachment_path}`} target="_blank" rel="noopener noreferrer">
                                <FileText className="h-4 w-4" />
                              </a>
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales by Branches Tab */}
        <TabsContent value="sales-branches">
          <Card>
            <CardHeader>
              <CardTitle>Sales by Branches Report</CardTitle>
              <CardDescription>View sales performance across branches.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Start Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {salesStartDate ? format(salesStartDate, 'PPP') : 'Select start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={salesStartDate}
                        onSelect={setSalesStartDate}
                        defaultMonth={salesStartDate ?? new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">End Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {salesEndDate ? format(salesEndDate, 'PPP') : 'Select end date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={salesEndDate}
                        onSelect={setSalesEndDate}
                        defaultMonth={salesEndDate ?? new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Branch:</label>
                  <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadSalesByBranchesReport} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate Report'}
                </Button>
              </div>

              {salesByBranchesReport && salesByBranchesReport.summary && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Sales Amount</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{salesByBranchesReport.summary.total_sales_amount.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
                        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {salesByBranchesReport.summary.total_transactions}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Branches</CardTitle>
                        <Building className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {salesByBranchesReport.summary.branches}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-4">
                    <Button onClick={downloadSalesByBranchesPDF} disabled={!salesByBranchesReport.details?.length}>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>

                  {salesByBranchesReport.details?.map(branch => (
                    <Card key={branch.branch_id} className="mb-4">
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle>{branch.branch_name}</CardTitle>
                          <p className="text-sm text-muted-foreground">
                            Transactions: {branch.transactions}
                          </p>
                        </div>
                        <CardDescription>
                          Total Amount: {currencySymbol}{(branch.total_amount ?? 0).toFixed(2)}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Type</TableHead>
                              <TableHead>Reference</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {branch.sales.map(entry => {
                              const isLayaway = entry.type === 'layaway_payment';
                              const rowKey = isLayaway
                                ? `layaway-${entry.payment_id}`
                                : `sale-${entry.sale_id}`;
                              const typeLabel = isLayaway ? 'Layaway Payment' : 'Sale';
                              const reference = isLayaway
                                ? `Layaway #${entry.layaway_number ?? '—'} Payment`
                                : entry.invoice_number
                                  ? `Invoice #${entry.invoice_number}`
                                  : `Sale #${entry.sale_id}`;

                              return (
                                <TableRow key={rowKey}>
                                  <TableCell>{typeLabel}</TableCell>
                                  <TableCell>{reference}</TableCell>
                                  <TableCell>{new Date(entry.created_at).toLocaleString()}</TableCell>
                                  <TableCell className="text-right">{currencySymbol}{entry.total_amount.toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales by Item Tab */}
        <TabsContent value="sales-items">
          <Card>
            <CardHeader>
              <CardTitle>Sales by Item Report</CardTitle>
              <CardDescription>View sales performance by individual items over a specified period.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Start Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {itemStartDate ? format(itemStartDate, 'PPP') : 'Select start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={itemStartDate}
                        onSelect={setItemStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">End Date:</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-48 justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {itemEndDate ? format(itemEndDate, 'PPP') : 'Select end date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={itemEndDate}
                        onSelect={setItemEndDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Button onClick={loadSalesByItemReport} disabled={loading}>
                  {loading ? 'Loading...' : 'Generate Report'}
                </Button>
              </div>

              {salesByItemReport && salesByItemReport.summary && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Sales Amount</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {currencySymbol}{salesByItemReport.summary.total_sales_amount.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Quantity Sold</CardTitle>
                        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {Number(salesByItemReport.summary.total_quantity_sold).toLocaleString(undefined, {
                            maximumFractionDigits: 2
                          })}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Total Items</CardTitle>
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {salesByItemReport.summary.total_items}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-4">
                    <Button onClick={downloadSalesByItemPDF} disabled={!salesByItemReport.details?.length}>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>

                  {salesByItemReport.details?.map(item => (
                    <Card key={item.item_id} className="mb-4">
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle>{item.name}</CardTitle>
                          <p className="text-sm text-muted-foreground">
                            Total Quantity: {Number(item.total_quantity).toLocaleString(undefined, {
                              maximumFractionDigits: 2
                            })}
                          </p>
                        </div>
                        <CardDescription>
                          Total Amount: {currencySymbol}{(item.total_amount ?? 0).toFixed(2)} | Avg Price: {currencySymbol}{(item.average_price ?? 0).toFixed(2)}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Type</TableHead>
                              <TableHead>Reference</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Quantity</TableHead>
                              <TableHead className="text-right">Price</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {item.sales.map(sale => {
                              const isLayaway = sale.type === 'layaway_payment';
                              const rowKey = isLayaway
                                ? `layaway-${sale.layaway_number}-${sale.created_at}`
                                : `sale-${sale.sale_id}`;
                              const typeLabel = isLayaway ? 'Layaway Payment' : 'Sale';
                              const reference = isLayaway
                                ? `Layaway #${sale.layaway_number ?? '—'} Payment`
                                : sale.invoice_number
                                  ? `Invoice #${sale.invoice_number}`
                                  : `Sale #${sale.sale_id}`;

                              const baseQuantity = isLayaway
                                ? sale.display_quantity ?? sale.quantity
                                : sale.quantity;
                              const coverageText = isLayaway && typeof sale.payment_ratio === 'number'
                                ? ` (${(sale.payment_ratio * 100).toFixed(0)}%)`
                                : '';
                              const quantityDisplay = `${Number(baseQuantity).toLocaleString(undefined, {
                                maximumFractionDigits: isLayaway ? 2 : 0
                              })}${coverageText}`;

                              return (
                                <TableRow key={rowKey}>
                                  <TableCell>{typeLabel}</TableCell>
                                  <TableCell>{reference}</TableCell>
                                  <TableCell>{new Date(sale.created_at).toLocaleString()}</TableCell>
                                  <TableCell>{quantityDisplay}</TableCell>
                                  <TableCell className="text-right">{currencySymbol}{sale.price.toFixed(2)}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <StatementDetailDialog
        open={detailDialog.open}
        onOpenChange={(open) => setDetailDialog(prev => ({ ...prev, open }))}
        type={detailDialog.type}
        startDate={detailDialog.startDate}
        endDate={detailDialog.endDate}
        label={detailDialog.label}
        categoryId={detailDialog.categoryId}
        categoryName={detailDialog.categoryName}
        currencySymbol={currencySymbol}
      />
    </div>
  );
}
